import { Component, OnInit, Input, OnDestroy } from '@angular/core';
import { FormComponentLookup, FormPageComponentClass, EntryConfigInterface, DefaultFormConfig } from '@ptl/forms';
import { ReducerUtilityService } from 'src/app/shared/utility-services/reducer-utility.service';
import { ConfirmationPageUrls } from 'src/app/angular-elements/shared/components/interfaces/confirmation-page-urls.interface';
import { IndividualPlanEnrollmentService } from './services/individual-plan-enrollment.service';
import { IndividualMarketsEnrollmentSubmissionService } from 'src/app/angular-elements/shared/services/enrollment/individual-markets/enrollment-submission.individual-markets.service';
import { GlobalDataStateStructure } from 'src/app/angular-elements/shared/components/configs/interfaces/globalStateStructure.interface';
import { IndividualMarketConsumerReferralEmailService } from 'src/app/angular-elements/shared/components/individual-market-consumer-referral-email/services/individual-market-consumer-referral-email.service';
import { LinkShareValues } from 'src/app/angular-elements/shared/components/individual-market-consumer-referral-email/services/link-share-values.interface';
import { saveLinkShareValues } from 'src/app/angular-elements/shared/components/individual-market-consumer-referral-email/services/link-share.reducer';
import { saveAgencyAndAgentValues } from 'src/app/angular-elements/shared/components/individual-market-consumer-referral-email/services/link-share.reducer';
import { ShopXFormPageActionsService } from '../../../services/shopx-form-page-actions.service';
import { SaveQuoteModalComponent } from 'src/app/angular-elements/producer/save-quote-modal/save-quote-modal.component';
import { MatDialogRef } from '@angular/material/types/_dialog-chunk';
import { combineLatest, filter, firstValueFrom, map, Observable, Subscription } from 'rxjs';
import { PtlWindowLocationService } from '@ptl/global-components';
import { IndividualPlanEnrollmentFormNavigationConfig } from './interfaces/individual-plan-enrollment.form-config.interface';
import { SaveQuoteModalConfigurationData } from 'src/app/angular-elements/producer/save-quote-modal/interfaces/save-quote-modal.interface';
import { UserInfoService } from 'src/app/angular-elements/shared/services/user-info.service';
import { select } from '@ptl/angular-redux-store';
import moment from 'moment';
import { DateService } from 'src/app/shared/services/date.service';

@FormComponentLookup('page-individual-plan-enroll')
@Component({
    selector: 'app-page-individual-plan-enrollment',
    templateUrl: './page-individual-plan-enrollment.component.html',
    styleUrls: ['./page-individual-plan-enrollment.component.scss'],
    standalone: false
})
export class PageIndividualPlanEnrollmentComponent extends FormPageComponentClass<IndividualPlanEnrollmentFormNavigationConfig> implements OnInit, OnDestroy {

  @Input() formConfigStrategy: string;

  @select(['payloadStates', 'individualMarket', 'ratingInfo', 'subscriber', '0', 'firstName']) firstNameSubscriber$: Observable<string>;
  firstNameSubscriberSubscription: Subscription;
  
  @select(['payloadStates', 'individualMarket', 'ratingInfo', 'subscriber', '0', 'lastName']) lastNameSubscriber$: Observable<string>;
  lastNameSubscriberSubscription: Subscription;
  
  @select(['payloadStates', 'individualMarket', 'ratingInfo', 'spousal-partner', '0', 'firstName']) firstNameSpouse$: Observable<string>;
  firstNameSpouseSubscription: Subscription;
  
  @select(['payloadStates', 'individualMarket', 'ratingInfo', 'spousal-partner', '0', 'lastName']) lastNameSpouse$: Observable<string>;
  lastNameSpouseSubscription: Subscription;

  @select(['payloadStates', 'individualMarket', 'ratingInfo', 'subscriber', '0', 'addressLine1'])
  addressLine1$: Observable<string>;

  @select(['payloadStates', 'individualMarket', 'ratingInfo', 'subscriber', '0', 'city'])
  city$: Observable<string>;

  @select(['payloadStates', 'individualMarket', 'ratingInfo', 'subscriber', '0', 'addressLine2'])
  addressLine2$: Observable<string>;

  @select(['payloadStates', 'individualMarket', 'ratingInfo', 'subscriber', '0', 'zipCode'])
  zipCode$: Observable<string>;

  @select(['payloadStates', 'individualMarket', 'ratingInfo', 'subscriber', '0', 'state'])
  state$: Observable<string>;

  // Combine all observables
  combinedLocationSubscription: Subscription; // Observable<{city: string, state: string, zipCode: string, addressLine1: string, addressLine2: string}>

  confirmationPageUrls: ConfirmationPageUrls;
  response: any;
  spousePage;
  dependentsPage;
  dependentsChildOnlyPage;
  childOnlySummaryPage;
  isSubmitting = false;

  constructor(
    private reducerUtilityService: ReducerUtilityService,
    private individualPlanEnrollmentService: IndividualPlanEnrollmentService,
    private enrollmentSubmitIndividual: IndividualMarketsEnrollmentSubmissionService,
    private individalMarketConsumerRefferalEmail: IndividualMarketConsumerReferralEmailService,
    private windowLocationService: PtlWindowLocationService,
    private userInfoService: UserInfoService,
    private dateService: DateService
  ) {
    super();

    // Either this is completely purposeless or it's super necessary to keep the instance of the validators alive
    this.individualPlanEnrollmentService.firstNameValidationSubscriber;
    this.individualPlanEnrollmentService.middleNameValidationSubscriber;
    this.individualPlanEnrollmentService.lastNameValidationSubscriber;
    this.individualPlanEnrollmentService.firstNameValidationSpouse;
    this.individualPlanEnrollmentService.middleNameValidationSpouse;
    this.individualPlanEnrollmentService.lastNameValidationSpouse;
    this.individualPlanEnrollmentService.firstNameValidationProducer;
    this.individualPlanEnrollmentService.middleNameValidationProducer;
    this.individualPlanEnrollmentService.lastNameValidationProducer;

  }

  ngOnDestroy(): void {
    this.firstNameSubscriberSubscription?.unsubscribe();
    this.lastNameSubscriberSubscription?.unsubscribe();
    this.firstNameSpouseSubscription?.unsubscribe();
    this.lastNameSpouseSubscription?.unsubscribe();
    this.combinedLocationSubscription?.unsubscribe();
  }

  async ngOnInit(): Promise<void> {
    await this.setProducerCollaborationValues();
    this.initPageConfig();
    this.observeAndReplaceTextTokens();
  }

  checkGroupValidations(formConfig: DefaultFormConfig): boolean {
    const currPage = formConfig.pages[this.currentPage];
    if (
      currPage &&
      currPage.custom &&
      currPage.custom.groupValidations
    ) {

      for (const groupValidation of currPage.custom.groupValidations) {
        if (groupValidation && groupValidation.required) {
          let count = 0;
          for (const control of groupValidation.controls) {
            // get entry
            let entryControl: EntryConfigInterface;
            for (const entry of currPage.entries) {
              if (entry.formControlAttributes && entry.formControlAttributes.formControlName === control) {
                entryControl = entry;
              }
            }
            const val = this.pageFormGroups[groupValidation.formGroupName].controls[control].value;
            if ((entryControl.base.componentType.includes('checkbox') && val === true) || val) {
              count++;
            }
          }
          if (count < groupValidation.minRequired) {
            if (groupValidation.action) {
              for (const entry of currPage.entries) {
                if (entry.formControlAttributes && entry.formControlAttributes.formControlName === groupValidation.action.controlName) {
                  this.formStreamTrigger.setNestedProperty(entry, groupValidation.action.propertyName, groupValidation.action.value);
                }
              }
            }
            return false;
          }
        }
      }
    }
    return true;
  }

  async setProducerCollaborationValues() {
    const componentStates = this.reducerUtilityService.getComponentStates();
    const globalData: GlobalDataStateStructure = this.reducerUtilityService.getState('GlobalData');
    if (globalData?.urlParams?.linkShareToken
      && (globalData?.urlParams?.linkShareToken !== componentStates.linkShareValues.urlKey)) {

      const linkShareValues: LinkShareValues = await this.individalMarketConsumerRefferalEmail.getLinkShareEmailData(globalData?.urlParams?.linkShareToken);
      if (linkShareValues) {
        this.ngRedux.dispatch(saveLinkShareValues(linkShareValues));
      }
    }else if(!globalData?.urlParams?.linkShareToken){
      if (globalData?.urlParams?.agencyNumber && globalData?.urlParams?.agentCode) {
        
          this.ngRedux.dispatch(saveAgencyAndAgentValues(globalData?.urlParams?.agencyNumber, globalData?.urlParams?.agentCode));
      }

    }
  }

  nextHook(formConfig): Promise<any> {
    return new Promise((resolve, reject) => {
      if (this.checkGroupValidations(formConfig)) {
        this.formPageActions.nextHook(formConfig);
        resolve(resolve);
      }
    });
  }

  submitHook(formConfig: DefaultFormConfig): Promise<any> {
    const enrollmentPersonInfo = this.reducerUtilityService.getPayloadState('individualMarket');

    return new Promise(async (resolve, reject) => {
      if (this.isSubmitting) {
        reject();
        return;
      }

      this.submitLock(true);
      let isSuccessful: boolean = false;

      try {
        isSuccessful = await this.enrollmentSubmitIndividual.submitApp(this.formConfig.form.customConfigs.confirmationPageUrl);
      }
      catch (e) {
        console.log(e);
        isSuccessful = false;
      }

      // Only re-enable the button if there was an error to prevent multiple submits
      if (!isSuccessful) {
        this.submitLock(false);
      }

      resolve(true);
    });
  }

  submitLock(isLocked: boolean) {
    this.isSubmitting = isLocked;
    this.formStreamTrigger.triggerFormStream({ formControlName: null, type: 'submit-locked', value: isLocked });
  }

  async saveForLaterButton(): Promise<void> {
    await this.saveForLaterButtonHook(this.formConfig).then(() => { });
  }

  async saveForLaterButtonHook(formConfig): Promise<any> {
    return new Promise(async (resolve, reject) => {
      await this.formPageActions.saveForLaterHook(formConfig);
      resolve(true);
    });
  }

  /**
   * This exists for a specific scenario, but essentially mimicks the submission form validation but with the save-for-later event handler
   * 
   * The specific scenario: 
   * 1) Telesales agent starts flow
   * 2) Saves quote
   * 3) Sends referral to consumer to upload documents
   * 4) *** consumer needs to upload documents and hit submit ( with validation ) but not actual submit the quote **
   * 5) Saves quote
   * 
   */
  async saveWithFormValidations(): Promise<void> {
      const validation = this.validateFormGroups(this.pageFormGroups);

      if (validation) {
        const service: ShopXFormPageActionsService = this.formPageActions as ShopXFormPageActionsService; 
        const isLoggedIn = await firstValueFrom(this.userInfoService.isLoggedIn$);

        // PRODUCER
        if (isLoggedIn) {
          
          const saveQuoteModal: MatDialogRef<SaveQuoteModalComponent, SaveQuoteModalConfigurationData>
            = await service.saveForLaterHook(this.formConfig) as unknown as MatDialogRef<SaveQuoteModalComponent, SaveQuoteModalConfigurationData>;

          await firstValueFrom(saveQuoteModal.afterClosed());

          
        } else {
          // CONSUMER
          await service.saveForLaterHook(this.formConfig);
        }

        if (this.formConfig.form.customConfigs.confirmationPageUrl) {
          this.windowLocationService.goToUrl(this.formConfig.form.customConfigs.confirmationPageUrl);
        }
      }
  }

  /**
   * This function updates the replacement map, and trigger the swapping of text tokens with values
   */
  observeAndReplaceTextTokens() {
    
    // SUBSCRIBER
    this.firstNameSubscriberSubscription = this.firstNameSubscriber$.subscribe( v => {
      if (!v) return;
      this.formConfig.replacementMap['<<firstNameSubscriber>>'] = v;
      this.doReplacements();
    });
    this.lastNameSubscriberSubscription = this.lastNameSubscriber$.subscribe( v => {
      if (!v) return;
      this.formConfig.replacementMap['<<lastNameSubscriber>>'] = v;
      this.doReplacements();
    });
    this.combinedLocationSubscription = combineLatest([
      this.city$,
      this.state$,
      this.zipCode$,
      this.addressLine1$,
      this.addressLine2$
    ]).pipe(
      // 3. Filter for non-null values
      filter(([city, state, zipCode, addressLine1, addressLine2]) =>
        city !== null &&
        state !== null &&
        zipCode !== null &&
        addressLine1 !== null &&
        addressLine2 !== null
      ),
      // 4. Map to a desired object structure
      map(([city, state, zipCode, addressLine1, addressLine2]) => ({
        city: city as string, // Cast to string since we've filtered out nulls
        state: state as string,
        zipCode: zipCode as string,
        addressLine1: addressLine1 as string,
        addressLine2: addressLine2 as string,
      }))
    ).subscribe( v => {
      if (!v) return;
      if (!v.addressLine2) {
        this.formConfig.replacementMap['<<subscriberAddress>>'] = `${v.addressLine1}<br/>${v.city}, ${v.state}<br/>${v.zipCode}`;
      } else {
        this.formConfig.replacementMap['<<subscriberAddress>>'] = `${v.addressLine1}<br/>${v.addressLine2}<br/>${v.city}, ${v.state}<br/>${v.zipCode}`;
      }
      this.doReplacements();
    });

    // SPOUSE
    this.firstNameSpouseSubscription = this.firstNameSpouse$.subscribe( v => {
      if (!v) return;
      this.formConfig.replacementMap['<<firstNameSpouse>>'] = v;
      this.doReplacements();
    });
    this.lastNameSpouseSubscription = this.lastNameSpouse$.subscribe( v => {
      if (!v) return;
      this.formConfig.replacementMap['<<lastNameSpouse>>'] = v;
      this.doReplacements();
    });

    this.formConfig.replacementMap['<<today-as-MM/DD/YYYY>>'] = this.dateService.getTodayAsMoment().format('MM/DD/YYYY');
    this.doReplacements();
  }

  /**
   * Takes replacement map values and swaps the tokens with values
   */
  doReplacements() {    
    let replacementMap = this.formConfig.replacementMap;
    if (replacementMap) {
      for (let key in replacementMap) {
        if (replacementMap[key] != null) {
          this.formConfig.pages[this.currentPage].entries.forEach( entry => {
            if (entry?.formControlAttributes?.label?.includes(key)) {
              entry.formControlAttributes.label = entry.formControlAttributes.label.replace(new RegExp(key, 'g'), replacementMap[key]);
            }
            if (entry?.base?.text?.includes(key)) {
              entry.base.text = entry.base.text.replace(new RegExp(key, 'g'), replacementMap[key]);
            }
          })
        }
      }
    }
  }
}
